/**
 * App.tsx — admin.mypcb.com, the universal admin portal (docs/architecture/admin-portal-shape.md,
 * approved by Bill 2026-10-16).
 *
 * Auth guard wraps the whole app (<RoleGate>, admits an admin of ANY platform). Routing model:
 *   /                       -> the first platform the person administers
 *   /retail                 -> Retail home (domain tiles); six sections at /retail/{section}
 *   /retail/<domain>/<page> -> today's Retail editors (moved under /retail 2026-10-16)
 *   /hub/...                -> Innovation Hub module (src/platforms/hub)
 *   /loans/...              -> Loans module (src/platforms/loans, LOP-owned)
 *   /finance                -> "not yet available" until its module lands
 *   /shared/...             -> bank-wide reference data (holidays, branches, regions)
 * Every pre-2026-10-16 path (/identity/..., /system/..., /org/..., /history, ...) redirects to
 * its new address with the query string kept, so existing links and bookmarks keep working.
 * Pages, not pop-ups: every screen has its own address.
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { ChangeHistoryPage } from '@/modules/ChangeHistory/ChangeHistoryPage';

import { RoleGate } from '@/components/RoleGate';
import { ShellLayout } from '@/components/ShellLayout';

import { AdminHome } from '@/modules/Home/AdminHome';
import { IdentityShell } from '@/modules/Identity/IdentityShell';
import { UsersPage } from '@/modules/Identity/UsersPage';
import { RolesPage } from '@/modules/Identity/RolesPage';
import { BranchesPage } from '@/modules/Org/BranchesPage';
import { RegionsPage } from '@/modules/Org/RegionsPage';
import { ProductsShell } from '@/modules/Products/ProductsShell';
import { ProductsPage } from '@/modules/Products/ProductsPage';
import { FeaturesPage } from '@/modules/Products/FeaturesPage';
import { CardTypesPage } from '@/modules/Products/CardTypesPage';
import { ProductFeaturesPage } from '@/modules/Products/ProductFeaturesPage';
import { ProductCustomerTypesPage } from '@/modules/Products/ProductCustomerTypesPage';
import { ProductServiceAvailabilityPage } from '@/modules/Products/ProductServiceAvailabilityPage';
import { ProductFundingMethodsPage } from '@/modules/Products/ProductFundingMethodsPage';
import { ProductSummaryChipsPage } from '@/modules/Products/ProductSummaryChipsPage';
import { DepositRatesListPage, DepositRateTermsEditPage, DepositRateTierEditPage } from '@/modules/Products/DepositRatesPages';
import { SystemShell } from '@/modules/System/SystemShell';
import { CriticalSettingsPage } from '@/modules/System/CriticalSettingsPage';
import { AccountMaintenancePage } from '@/modules/System/AccountMaintenancePage';
import { SettingsPage } from '@/modules/System/SettingsPage';
import { SlaTargetsPage } from '@/modules/System/SlaTargetsPage';
import { JxChangeDiagnosticsPage } from '@/modules/System/JxChangeDiagnosticsPage';
import { KillListEntriesPage } from '@/modules/System/KillListEntriesPage';
import { PilotOfficersPage } from '@/modules/System/PilotOfficersPage';
import { CartRetentionPoliciesPage } from '@/modules/System/CartRetentionPoliciesPage';
import { RetentionPoliciesPage } from '@/modules/System/RetentionPoliciesPage';
import { AlertThresholdsPage } from '@/modules/System/AlertThresholdsPage';
import { RcpScaffoldPage } from '@/modules/System/RcpScaffoldPage';
import { PurgeReasonCodesPage } from '@/modules/System/PurgeReasonCodesPage';
import { ReferenceShell } from '@/modules/Reference/ReferenceShell';
import { CdPenaltyRulesPage } from '@/modules/Reference/CdPenaltyRulesPage';
import { LnRulesMatrixPage } from '@/modules/Reference/LnRulesMatrixPage';
import { CoreDirectorCodesPage } from '@/modules/Reference/CoreDirectorCodesPage';
import { ReviewAggregateChipsPage } from '@/modules/Reference/ReviewAggregateChipsPage';
import { HomeDashboardCardsPage } from '@/modules/Reference/HomeDashboardCardsPage';
import { NaicsCodesPage } from '@/modules/Reference/NaicsCodesPage';
import { StateGovSearchLinksPage } from '@/modules/Reference/StateGovSearchLinksPage';
import { ParmValSyncPage } from '@/modules/Reference/ParmValSyncPage';
import { IraReasonCodesPage } from '@/modules/Reference/IraReasonCodesPage';
import { IraTransactionCodesPage } from '@/modules/Reference/IraTransactionCodesPage';
import { IraContributionLimitsPage } from '@/modules/Reference/IraContributionLimitsPage';
import { IraInstitutionRelationshipsPage } from '@/modules/Reference/IraInstitutionRelationshipsPage';
import { IraContributionPhaseoutsPage } from '@/modules/Reference/IraContributionPhaseoutsPage';
import { NotificationsShell } from '@/modules/Notifications/NotificationsShell';
import { EventBindingsPage } from '@/modules/Notifications/EventBindingsPage';
import { NotificationOptOutsPage } from '@/modules/Notifications/NotificationOptOutsPage';
import { NotificationDispatchHistoryPage } from '@/modules/Notifications/NotificationDispatchHistoryPage';
import { NotificationTemplatesPage } from '@/modules/Notifications/NotificationTemplatesPage';
import { ComplianceShell } from '@/modules/Compliance/ComplianceShell';
import { CipIdPriorityPage } from '@/modules/Compliance/CipIdPriorityPage';
import { ConflictRulesPage } from '@/modules/Compliance/ConflictRulesPage';
import { MarketAreaZipsPage } from '@/modules/Compliance/MarketAreaZipsPage';
import { DiModelBindingPage } from '@/modules/Compliance/DiModelBindingPage';
import { GateEnforcementPage } from '@/modules/Compliance/GateEnforcementPage';
import { ComplianceGatesPage } from '@/modules/Compliance/ComplianceGatesPage';
import { ProcedureCitationsPage } from '@/modules/Compliance/ProcedureCitationsPage';
import { GateKeysPage } from '@/modules/Compliance/GateKeysPage';
import { EntityRulesPage } from '@/modules/Compliance/EntityRulesPage';
import { DeferredGateTrackingPage } from '@/modules/Compliance/DeferredGateTrackingPage';
import { LexisNexisConfigPage } from '@/modules/Compliance/LexisNexisConfigPage';
import { LexisNexisCallLogPage } from '@/modules/Compliance/LexisNexisCallLogPage';
import { PepSettingsPage } from '@/modules/Compliance/PepSettingsPage';
import { WorkflowShell } from '@/modules/Workflow/WorkflowShell';
import { SigningEngineRoutingPage } from '@/modules/Workflow/SigningEngineRoutingPage';
import { SignatureAuthRulesPage } from '@/modules/Workflow/SignatureAuthRulesPage';
import { WetSignAttestationBoxPage } from '@/modules/Workflow/WetSignAttestationBoxPage';
import { TaskTemplatesPage } from '@/modules/Workflow/TaskTemplatesPage';
import { WeeklyTaskCriteriaPage } from '@/modules/Workflow/WeeklyTaskCriteriaPage';
import { DocumentsShell } from '@/modules/Documents/DocumentsShell';
import { IraDisclosureBodiesPage } from '@/modules/Documents/IraDisclosureBodiesPage';
import { DisclosureTemplateLifecyclePage } from '@/modules/Documents/DisclosureTemplateLifecyclePage';
import { DocumentRequirementsPage } from '@/modules/Documents/DocumentRequirementsPage';

import { PlatformsProvider } from '@/contexts/PlatformsContext';
import { PortalHome } from '@/platforms/PortalHome';
import { PlatformPlaceholder } from '@/platforms/PlatformLayout';
import { AboutPage } from '@/modules/About/AboutPage';
import { RetailArea, RetailSectionPage } from '@/platforms/retail/RetailArea';
import { HubArea, HubHome, HubSectionPage } from '@/platforms/hub/HubArea';
import { LoansArea, LoansHome, LoansSectionPage } from '@/platforms/loans/LoansArea';
import { LOANS_WRITEUP_PAGES } from '@/platforms/loans/writeups';
import { EncompassConfigPage } from '@/platforms/loans/encompass/EncompassConfigPage';
import { LoansSettingsPage } from '@/platforms/loans/settings/LoansSettingsPage';
import { SplashNoticesPage } from '@/platforms/loans/notices/SplashNoticesPage';
import { AccessRolesPage } from '@/platforms/loans/access/AccessRolesPage';
import { HubSettingEditPage, HubSettingsListPage } from '@/platforms/hub/HubSettingsPages';
import { HubAuditPage, HubDeliveriesPage } from '@/platforms/hub/HubMonitoringPages';
import { HubTestCardPage } from '@/platforms/hub/HubTestCardPage';
import { RetailBotTestCardPage } from '@/platforms/retail/tools/BotTestCardPage';
import { SharedArea } from '@/platforms/shared/SharedArea';
import { HolidayEditPage, HolidaysListPage } from '@/platforms/shared/HolidaysPages';

// Redirects from every pre-2026-10-16 path (tested in routes/legacyRedirects.test.tsx).
import { LEGACY_RETAIL_DOMAINS, OrgToShared, ToRetail } from '@/routes/legacyRedirects';

export function App(): JSX.Element {
  return (
    <PlatformsProvider>
    <RoleGate>
      <BrowserRouter basename="/">
        <Routes>
          <Route element={<ShellLayout />}>
            <Route index element={<PortalHome />} />

            {/* ── Retail Operations ─────────────────────────────────────── */}
            <Route path="retail" element={<RetailArea />}>
              <Route index element={<AdminHome />} />
              {/* Bill 2026-10-08 — every configuration change, searchable and restorable. */}
              <Route path="history" element={<ChangeHistoryPage />} />

              {/* Domain 1 — Identity & Access. Sync is a header-level modal (v0.8.6). */}
              <Route path="identity" element={<IdentityShell />}>
                <Route index element={<Navigate to="users" replace />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="roles" element={<RolesPage />} />
                {/* v0.8.6 — old #/identity/sync URL bookmarks redirect to Users. */}
                <Route path="sync" element={<Navigate to="/retail/identity/users" replace />} />
              </Route>

              {/* Domain 3 — Products & Features */}
              <Route path="products" element={<ProductsShell />}>
                <Route index element={<Navigate to="products" replace />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="features" element={<FeaturesPage />} />
                <Route path="card-types" element={<CardTypesPage />} />
                <Route path="product-features" element={<ProductFeaturesPage />} />
                <Route path="product-customer-types" element={<ProductCustomerTypesPage />} />
                <Route path="product-service-availability" element={<ProductServiceAvailabilityPage />} />
                <Route path="product-funding-methods" element={<ProductFundingMethodsPage />} />
                <Route path="product-summary-chips" element={<ProductSummaryChipsPage />} />
                {/* Bill 2026-10-16 — Truth-in-Savings rates printed on the Rate and Fee Schedule (RATE_SCHED 1.2). */}
                <Route path="deposit-rates" element={<DepositRatesListPage />} />
                <Route path="deposit-rates/tiers/:id" element={<DepositRateTierEditPage />} />
                <Route path="deposit-rates/products/:code" element={<DepositRateTermsEditPage />} />
              </Route>

              {/* Domain 4 — System. Settings landed v0.9.6; SLA Targets v0.9.7.
                  KillList slots in later. */}
              <Route path="system" element={<SystemShell />}>
                <Route index element={<Navigate to="critical-settings" replace />} />
                <Route path="critical-settings" element={<CriticalSettingsPage />} />
                {/* Bill 2026-10-17 — account maintenance switches (all off pending the ruling). */}
                <Route path="account-maintenance" element={<AccountMaintenancePage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="sla-targets" element={<SlaTargetsPage />} />
                <Route path="jxchange-diagnostics" element={<JxChangeDiagnosticsPage />} />
                {/* Migrated from ROP-teams Admin 2026-06-16 (BACKLOG-0579/0101,
                    parallel dispatch). PurgeReasonCodes pending its backend route. */}
                <Route path="kill-list" element={<KillListEntriesPage />} />
                <Route path="pilot-officers" element={<PilotOfficersPage />} />
                <Route path="cart-retention-policies" element={<CartRetentionPoliciesPage />} />
                <Route path="retention-policies" element={<RetentionPoliciesPage />} />
                <Route path="alert-thresholds" element={<AlertThresholdsPage />} />
                <Route path="rcp" element={<RcpScaffoldPage />} />
                {/* PurgeReasonCodes — backend write route wired 2026-06-17 (was GET-only). */}
                <Route path="purge-reason-codes" element={<PurgeReasonCodesPage />} />
              </Route>

              {/* Domain 5 — Reference Data (v0.9.8). CD Penalty Rules is the
                  first sub-tab; LN Verification Rules added v0.9.13 (BACKLOG-0501);
                  NAICS / IRA Reason Codes / IRA Contribution Limits / Core
                  Director enums slot in later. */}
              <Route path="reference" element={<ReferenceShell />}>
                <Route index element={<Navigate to="cd-penalty-rules" replace />} />
                <Route path="cd-penalty-rules" element={<CdPenaltyRulesPage />} />
                <Route path="ln-verification-rules" element={<LnRulesMatrixPage />} />
                <Route path="core-director-codes" element={<CoreDirectorCodesPage />} />
                <Route path="review-aggregate-chips" element={<ReviewAggregateChipsPage />} />
                <Route path="home-dashboard-cards" element={<HomeDashboardCardsPage />} />
                {/* Reference bulk migrated from ROP-teams Admin 2026-06-16
                    (BACKLOG-0579/0101) via parallel dispatch. */}
                <Route path="naics-codes" element={<NaicsCodesPage />} />
                <Route path="state-gov-search-links" element={<StateGovSearchLinksPage />} />
                <Route path="parmval-sync" element={<ParmValSyncPage />} />
                <Route path="ira-reason-codes" element={<IraReasonCodesPage />} />
                <Route path="ira-transaction-codes" element={<IraTransactionCodesPage />} />
                <Route path="ira-contribution-limits" element={<IraContributionLimitsPage />} />
                <Route path="ira-institution-relationships" element={<IraInstitutionRelationshipsPage />} />
                <Route path="ira-contribution-phaseouts" element={<IraContributionPhaseoutsPage />} />
              </Route>

              {/* Domain 6 — Tasks & Templates / Notifications. Event Bindings +
                  Opt-Outs + Dispatch History migrated from ROP-teams Admin
                  (2026-06-16); NotificationTemplates + Task Templates slot in later. */}
              <Route path="notifications" element={<NotificationsShell />}>
                <Route index element={<Navigate to="event-bindings" replace />} />
                <Route path="event-bindings" element={<EventBindingsPage />} />
                <Route path="templates" element={<NotificationTemplatesPage />} />
                <Route path="opt-outs" element={<NotificationOptOutsPage />} />
                <Route path="dispatch-history" element={<NotificationDispatchHistoryPage />} />
              </Route>

              {/* Domain — Workflow (new module 2026-06-16, BACKLOG-0579/0101).
                  Signing Engine Routing migrated from ROP-teams Admin (the prior
                  Slot-C violation); signatures / wet-sign / tasks slot in next. */}
              <Route path="workflow" element={<WorkflowShell />}>
                <Route index element={<Navigate to="signing-engine-routing" replace />} />
                <Route path="signing-engine-routing" element={<SigningEngineRoutingPage />} />
                <Route path="signature-auth-rules" element={<SignatureAuthRulesPage />} />
                <Route path="wet-sign-attestation-box" element={<WetSignAttestationBoxPage />} />
                <Route path="task-templates" element={<TaskTemplatesPage />} />
                <Route path="weekly-task-criteria" element={<WeeklyTaskCriteriaPage />} />
              </Route>

              {/* Domain — Documents & Disclosures (new module 2026-06-16,
                  BACKLOG-0579/0100). Disclosure Bodies + Template Lifecycle
                  migrated from ROP-teams Admin; Document Requirements (the
                  Disclosure-Engine matrix) slots in next. */}
              <Route path="documents" element={<DocumentsShell />}>
                <Route index element={<Navigate to="document-requirements" replace />} />
                <Route path="disclosure-bodies" element={<IraDisclosureBodiesPage />} />
                <Route path="template-lifecycle" element={<DisclosureTemplateLifecyclePage />} />
                <Route path="document-requirements" element={<DocumentRequirementsPage />} />
              </Route>

              {/* Domain 7 — Compliance & Capture (Person-Modal deep-clean, Slot
                  TL). CIP ID Priority + Conflict Rules + Document Intelligence +
                  Gate Enforcement. Consumes Slot TR's Contract C5 config routes. */}
              <Route path="compliance" element={<ComplianceShell />}>
                <Route index element={<Navigate to="cip-id-priority" replace />} />
                <Route path="cip-id-priority" element={<CipIdPriorityPage />} />
                <Route path="conflict-rules" element={<ConflictRulesPage />} />
                <Route path="market-area-zips" element={<MarketAreaZipsPage />} />
                <Route path="document-intelligence" element={<DiModelBindingPage />} />
                <Route path="gate-enforcement" element={<GateEnforcementPage />} />
                <Route path="compliance-gates" element={<ComplianceGatesPage />} />
                <Route path="procedure-citations" element={<ProcedureCitationsPage />} />
                <Route path="gate-keys" element={<GateKeysPage />} />
                <Route path="entity-rules" element={<EntityRulesPage />} />
                {/* Deferred Gate Tracking + LexisNexis Config/Call-Log migrated
                    from ROP-teams Admin 2026-06-16 (BACKLOG-0579/0100). */}
                <Route path="deferred-gate-tracking" element={<DeferredGateTrackingPage />} />
                <Route path="lexisnexis-config" element={<LexisNexisConfigPage />} />
                <Route path="lexisnexis-call-log" element={<LexisNexisCallLogPage />} />
                {/* PEP control admin editor (Bill 2026-06-17) — writes the 4
                    pep.* SystemSettings keys read by GET /v3/config/pep. */}
                <Route path="pep-settings" element={<PepSettingsPage />} />
              </Route>
              {/* 0.54.0 (Bill 2026-11-03) — a real test card from the PCB Chat Bot to the signed-in person (pcb-retail-api 3.41.0). */}
              <Route path="tools/bot-test-card" element={<RetailBotTestCardPage />} />
              <Route path=":section" element={<RetailSectionPage />} />
            </Route>

            {/* ── Innovation Hub (src/platforms/hub; contract 2026-10-16) ── */}
            <Route path="hub" element={<HubArea />}>
              <Route index element={<HubHome />} />
              <Route path="configuration/settings" element={<HubSettingsListPage section="configuration" />} />
              <Route path="configuration/settings/:key" element={<HubSettingEditPage section="configuration" />} />
              <Route path="access/settings" element={<HubSettingsListPage section="access" />} />
              <Route path="access/settings/:key" element={<HubSettingEditPage section="access" />} />
              <Route path="monitoring/audit" element={<HubAuditPage />} />
              <Route path="monitoring/deliveries" element={<HubDeliveriesPage />} />
              {/* 0.54.0 — a real test ask card from the Innovation Chat Bot to the signed-in admin (pcb-innovation-api 0.13.0). */}
              <Route path="tools/test-card" element={<HubTestCardPage />} />
              <Route path=":section" element={<HubSectionPage />} />
            </Route>

            {/* ── Platforms whose modules have not landed yet ───────────── */}
            {/* ── Loans (src/platforms/loans, LOP-owned; Bill 2026-10-29) ── */}
            <Route path="loans" element={<LoansArea />}>
              <Route index element={<LoansHome />} />
              {/* LWP write-up pages (src/platforms/loans/writeups); one route per entry */}
              {Object.values(LOANS_WRITEUP_PAGES).map((p) => (
                <Route key={p.path} path={p.path} element={<p.element />} />
              ))}
              <Route path="configuration/encompass" element={<EncompassConfigPage />} />
              <Route path="configuration/settings" element={<LoansSettingsPage />} />
              <Route path="configuration/notices" element={<SplashNoticesPage />} />
              <Route path="access/people" element={<AccessRolesPage />} />
              <Route path=":section" element={<LoansSectionPage />} />
            </Route>
            <Route path="finance/*" element={<PlatformPlaceholder name="Finance" />} />

            {/* ── About: software versions and support dates (0.51.0) ─────── */}
            <Route path="about" element={<AboutPage />} />

            {/* ── Shared reference data (Retail owns; every platform reads) ─ */}
            <Route path="shared" element={<SharedArea />}>
              <Route index element={<Navigate to="holidays" replace />} />
              <Route path="holidays" element={<HolidaysListPage />} />
              <Route path="holidays/:id" element={<HolidayEditPage />} />
              <Route path="branches" element={<BranchesPage />} />
              <Route path="regions" element={<RegionsPage />} />
            </Route>

            {/* ── Redirects from every pre-2026-10-16 path ───────────────── */}
            {LEGACY_RETAIL_DOMAINS.map((d) => (
              <Route key={d} path={`${d}/*`} element={<ToRetail />} />
            ))}
            <Route path="org/*" element={<OrgToShared />} />

            {/* MSAL redirectUri is the app root, so we don't need a
                dedicated callback route — the in-flight MSAL redirect is
                finalized by ensureMsalReady() on every page load. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </RoleGate>
    </PlatformsProvider>
  );
}
